import { createStyles } from '@/contexts/theme-context';
import { type FC, useState } from 'react';
import { FlashList } from '@shopify/flash-list';
import { getSubjectTranslation } from '@/screens/order-inbounds/utils';
import {
  InboundMetadataContactRow,
  InboundMetadataDateRow,
  InboundMetadataRows,
  InboundMetadataSubjectRow,
} from '@/screens/order-inbounds/inbound-metadata-rows/inbound-metadata-rows';
import {
  OrderInboundsDetailEmlContent,
  type Props as OrderInboundsDetailEmlContentProps,
} from '@/screens/order-inbounds/order-inbounds-detail-tabs/order-inbounds-detail-eml-content/order-inbounds-detail-eml-content';
import { OrderInboundsDetailEmlHeaderStatusBadge } from './order-inbounds-detail-eml-content/order-inbounds-detail-eml-header-status-badge';
import { type OrderInboundsDetailTabsEmailThreadRoute } from '@/screens/order-inbounds/order-inbounds-detail-tabs/types';
import { StyleSheet, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';

const EMAIL_THREAD_ITEM_ESTIMATED_SIZE = 300;

const OrderInboundsDetailEmailThreadItemSeparator: FC = () => {
  const [styles] = useSeparatorStyles();

  return <View style={styles.container} />;
};

const useSeparatorStyles = createStyles((theme) => ({
  container: {
    backgroundColor: theme.sk.color.divider.default,
    height: 4,
    width: '100%',
  },
}));

interface Props
  extends Omit<
    OrderInboundsDetailEmlContentProps,
    'attachment' | 'isCollapsed' | 'onCollapsedChange' | 'order'
  > {
  items: OrderInboundsDetailTabsEmailThreadRoute['items'];
}

export const OrderInboundsDetailEmailThreadContent: FC<Props> = ({
  containerWidth,
  items,
  onAreaSelect,
  onAttachmentListItemPress,
  onEmlError,
  onHighlightElementSelect,
  onHtmlHasNoContent,
  onInlineImageClick,
  selectedAreaID,
  showTranslation,
  translationToggle,
}) => {
  const isEmailThreadsEnabled = useSupplierDynamicConfigFlag(
    'smart_order_config',
    'email_threads_enabled',
  );

  const { isSmallLayout, isSmallTabletLayout } = useLayoutContext();

  const [collapsedState, setCollapsedState] = useState<Record<string, boolean>>(
    {},
  );

  return (
    <FlashList
      data={items}
      drawDistance={250}
      estimatedItemSize={EMAIL_THREAD_ITEM_ESTIMATED_SIZE}
      extraData={collapsedState}
      ItemSeparatorComponent={OrderInboundsDetailEmailThreadItemSeparator}
      keyExtractor={(item) => item.attachment.url}
      renderItem={({ item, index }) => (
        <View style={styles.emailThreadItemContainer}>
          <OrderInboundsDetailEmlContent
            attachment={item.attachment}
            collapsible={index !== 0}
            containerWidth={containerWidth}
            HeaderComponent={
              <View
                style={[
                  styles.headerContainer,
                  isEmailThreadsEnabled && styles.headerContainerRow,
                  isEmailThreadsEnabled &&
                    (isSmallLayout || isSmallTabletLayout) &&
                    styles.headerContainerRowMobile,
                ]}
              >
                {index === 0 && Boolean(item.orderMetadata.subject) ? (
                  <>
                    <Typography variant="heading-md">
                      {showTranslation
                        ? (getSubjectTranslation(item.orderMetadata) ??
                          item.orderMetadata.subject)
                        : item.orderMetadata.subject}
                    </Typography>
                  </>
                ) : index >= 1 ? (
                  <>
                    <InboundMetadataRows orders={[item.orderMetadata]}>
                      <InboundMetadataContactRow />
                      <InboundMetadataDateRow showFirstDateOnly />
                      <InboundMetadataSubjectRow />
                    </InboundMetadataRows>
                  </>
                ) : null}

                {isEmailThreadsEnabled && items.length > 1 ? (
                  <OrderInboundsDetailEmlHeaderStatusBadge
                    order={item.orderMetadata}
                  />
                ) : null}
              </View>
            }
            isCollapsed={collapsedState[item.attachment.url] ?? false}
            onAreaSelect={onAreaSelect}
            onAttachmentListItemPress={onAttachmentListItemPress}
            onCollapsedChange={(value) =>
              setCollapsedState((prev) => ({
                ...prev,
                [item.attachment.url]: value,
              }))
            }
            onEmlError={() => onEmlError?.(item.attachment)}
            onHighlightElementSelect={onHighlightElementSelect}
            onHtmlHasNoContent={() => onHtmlHasNoContent?.(item.attachment)}
            onInlineImageClick={onInlineImageClick}
            order={item.orderMetadata}
            selectedAreaID={selectedAreaID}
            showTranslation={showTranslation}
            translationToggle={translationToggle}
          />
        </View>
      )}
    />
  );
};

const styles = StyleSheet.create({
  emailThreadItemContainer: {
    padding: 24,
  },
  headerContainer: {
    paddingBottom: 24,
  },
  headerContainerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  headerContainerRowMobile: {
    flexDirection: 'column-reverse',
    gap: 8,
  },
});
